/**
 * DL-01 decision 2 — the project settings card "Customers may download
 * full-resolution images": the owner's per-project switch
 * (`projects.customer_image_download`, default OFF) that lets the customer
 * tenant's `project_customer` grant download the images of the project's
 * data sets (story §B, last line).
 *
 * Written through `PATCH /projects/{id}/customer-image-download`
 * (projects/v1 2.27.0, `useSetCustomerImageDownloadMutation`) by the owner's
 * tenant_admin / project_manager (platform_admin) only — a customer gets a
 * 404 there, so the switch is HIDDEN for everyone else and on a member
 * project: the card then reads the stored value only.
 *
 * The hot-tier note (decision 3): a customer is blocked once the images left
 * the Hot tier — the owner is not; the switch does not change that.
 *
 * The field `customerImageDownload` is HAND-TYPED on `ProjectDetail` /
 * `ProjectListItem` until the pin bump (`types.ts`).
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Alert,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { container, radius } from "@aeriosiq/ui";
import { useFreshRoles } from "../../sites/shared/useFreshRoles.js";
import {
  useProjectDetailQuery,
  useSetCustomerImageDownloadMutation,
} from "../useProjects.js";

/** Who may flip the switch — mirrors the BE gate (§A-4, 2.27.0). */
const FLAG_WRITE_ROLES: ReadonlySet<string> = new Set([
  "tenant_admin",
  "project_manager",
  "platform_admin",
]);

export const CUSTOMER_IMAGE_DOWNLOAD_WORDS = {
  title: "Customer image download",
  switch: "Customers may download full-resolution images",
  help: "When on, the customer tenant can download the original images of this project's data sets to a folder on their PC — from the data set, or all data sets of an instruction. Off by default.",
  hotTier:
    "Customers can only download images that are still in the Hot storage tier; once images are archived, the customer sees an explanation and asks you — you are not blocked and pay the retrieval.",
  stored: (on: boolean): string =>
    on
      ? "Customers may download full-resolution images (set by the project's owner)."
      : "Customers may not download full-resolution images (set by the project's owner).",
  readOnly:
    "Only a tenant admin or project manager of the owning tenant can change this.",
  saved: "Saved.",
  failed: "Could not save the setting.",
  loadFailed: "Could not load the project's settings.",
} as const;

export interface CustomerImageDownloadCardProps {
  projectId: string;
  /** The project is the caller's own (a member project renders read-only). */
  isOwnerProject?: boolean | undefined;
}

export function CustomerImageDownloadCard({
  projectId,
  isOwnerProject = true,
}: CustomerImageDownloadCardProps): ReactElement {
  const roles = useFreshRoles();
  const canWrite = isOwnerProject && roles.some((r) => FLAG_WRITE_ROLES.has(r));
  const detail = useProjectDetailQuery(projectId);
  const set = useSetCustomerImageDownloadMutation(projectId);
  const stored = detail.data?.customerImageDownload ?? false;
  const [value, setValue] = useState(stored);
  const [note, setNote] = useState<"saved" | "failed" | null>(null);
  useEffect(() => {
    setValue(stored);
  }, [stored]);

  const onToggle = (next: boolean): void => {
    setValue(next);
    setNote(null);
    set.mutate(next, {
      onSuccess: () => {
        setNote("saved");
      },
      onError: () => {
        setValue(stored);
        setNote("failed");
      },
    });
  };

  if (detail.isPending) {
    return (
      <Stack alignItems="center" sx={{ py: 3 }}>
        <CircularProgress aria-label="Loading customer image download setting" />
      </Stack>
    );
  }
  if (detail.isError) {
    return (
      <Alert severity="error" data-testid="customer-image-download-error">
        {CUSTOMER_IMAGE_DOWNLOAD_WORDS.loadFailed}
      </Alert>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, borderRadius: radius.card, maxWidth: container.prose }}
      data-testid="customer-image-download-card"
    >
      <Stack spacing={1.5}>
        <Typography variant="h6">
          {CUSTOMER_IMAGE_DOWNLOAD_WORDS.title}
        </Typography>
        {canWrite ? (
          <FormControlLabel
            control={
              <Switch
                checked={value}
                disabled={set.isPending}
                onChange={(e) => {
                  onToggle(e.target.checked);
                }}
                inputProps={{
                  "aria-label": CUSTOMER_IMAGE_DOWNLOAD_WORDS.switch,
                }}
                data-testid="customer-image-download-switch"
              />
            }
            label={CUSTOMER_IMAGE_DOWNLOAD_WORDS.switch}
          />
        ) : (
          <Stack spacing={0.5} data-testid="customer-image-download-readonly">
            <Typography
              variant="body2"
              data-testid="customer-image-download-stored"
            >
              {CUSTOMER_IMAGE_DOWNLOAD_WORDS.stored(stored)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {CUSTOMER_IMAGE_DOWNLOAD_WORDS.readOnly}
            </Typography>
          </Stack>
        )}
        <Typography variant="body2" color="text.secondary">
          {CUSTOMER_IMAGE_DOWNLOAD_WORDS.help}
        </Typography>
        <Alert
          severity="info"
          variant="outlined"
          data-testid="customer-image-download-hot-tier"
        >
          {CUSTOMER_IMAGE_DOWNLOAD_WORDS.hotTier}
        </Alert>
        {note === "saved" && (
          <Typography
            variant="body2"
            color="success.main"
            data-testid="customer-image-download-saved"
          >
            {CUSTOMER_IMAGE_DOWNLOAD_WORDS.saved}
          </Typography>
        )}
        {note === "failed" && (
          <Alert severity="error" data-testid="customer-image-download-failed">
            {CUSTOMER_IMAGE_DOWNLOAD_WORDS.failed}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
